import { IconName } from '@tallo/design-system';

import {
  ApplicationInviteSentTracePayload,
  ApplicationSubmittedTracePayload,
  ApplicationUpdateRequestTracePayload,
  ConversationTraceDto,
  ConversationTraceKind,
  EscalationAnswerTracePayload,
  FollowUpTracePayload,
  IdentityVerificationTracePayload,
  NewLeadTracePayload,
  OwnerActionTracePayload,
  QualificationTracePayload,
  SchedulingTracePayload,
  StrategyTracePayload,
} from '../models/conversation-trace.interface';
import { followUpMeta, hiddenSteps } from './trace-copy.const';
import { formatTraceDayTime, humanizeTraceKey, truncateTraceText } from './trace-format.functions';

export type TraceEventTone = 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'grey';

export interface TraceEventCopy {
  tone: TraceEventTone;
  icon: IconName;
  /** Bold lead-in ("You approved the tour"). */
  title: string;
  /** Regular continuation, rendered after an em-dash. */
  detail?: string;
  /** Verbatim words (an owner's reason, an escalated question), rendered quoted and muted. */
  quote?: string;
}

/**
 * Copy for one standalone trace (messageId null) → an event row on the rail, or null for traces
 * that must stay invisible. `timeZone` is the property's IANA zone — tour times render there, so
 * the row agrees with the renter-facing message beside it. Null matters: a fully silent turn (e.g. an emoji reaction where every
 * strategy returned no_answer) flushes its traces as standalone rows, and those must not become
 * noise in the timeline.
 */
export function buildTraceEventCopy(
  trace: ConversationTraceDto,
  renterFirstName: string,
  timeZone?: string,
): TraceEventCopy | null {
  if (trace.kind === ConversationTraceKind.NEW_LEAD) {
    const payload = trace.payload as unknown as NewLeadTracePayload;
    const sourceDetails: Record<string, string> = {
      'Manually Added': 'added manually',
      'Apply Link': 'through your apply link',
    };
    // Both branches share the zap: one event kind, one badge, so the eye learns "a lead arrived"
    // without reading the row.
    return payload.source === 'Tallo'
      ? {
          tone: 'orange',
          icon: 'zap',
          title: 'Matched from the Tallo network',
          detail: 'already talking to Tallo about another property',
        }
      : {
          tone: 'orange',
          icon: 'zap',
          // "Manually Added" and "Apply Link" are enum values, not places: "from Manually Added" reads as a leak.
          title: 'New lead',
          detail: sourceDetails[payload.source] ?? `from ${payload.source || 'your listing'}`,
        };
  }

  if (trace.kind === ConversationTraceKind.QUALIFICATION) {
    const payload = trace.payload as unknown as QualificationTracePayload;
    // A standalone verdict is the fast-qualify path: screened before any message existed. No reason
    // text on the failed branch — it is close to the renter-facing message and reads as an echo.
    return payload.qualified
      ? {
          tone: 'green',
          icon: 'check',
          title: 'Already qualified',
          detail: 'screened in a previous Tallo conversation',
        }
      : { tone: 'red', icon: 'x-close', title: 'Not qualified', detail: 'screened in a previous Tallo conversation' };
  }

  if (trace.kind === ConversationTraceKind.OWNER_ACTION) {
    const payload = trace.payload as unknown as OwnerActionTracePayload;
    const showingTime = payload.showingTime ? formatTraceDayTime(payload.showingTime, timeZone) : undefined;
    switch (payload.action) {
      case 'approved':
        return { tone: 'green', icon: 'check', title: 'You approved the tour', detail: showingTime };
      case 'rescheduled':
        return {
          tone: 'blue',
          icon: 'calendar-reschedule',
          title: 'You moved the tour',
          detail: payload.proposedTime ? `to ${formatTraceDayTime(payload.proposedTime, timeZone)}` : undefined,
          quote: payload.reason,
        };
      case 'declined':
        return { tone: 'red', icon: 'x-close', title: 'You declined the tour request', quote: payload.reason };
      case 'canceled': {
        const who = payload.canceledBy === 'owner' ? 'You' : renterFirstName;
        return {
          tone: 'red',
          icon: 'x-close',
          title: `${who} canceled the tour`,
          detail: showingTime,
          quote: payload.reason,
        };
      }
      case 'expired':
        return {
          tone: 'grey',
          icon: 'clock-fast-forward',
          title: 'Request expired',
          detail: 'the tour was auto-canceled after no response',
        };
      case 'auto_canceled':
        return {
          tone: 'grey',
          icon: 'clock-fast-forward',
          title: 'Tour auto-canceled',
          detail: payload.reason || `${renterFirstName} didn't confirm attendance`,
        };
      case 'application_request_tour_first':
        return {
          tone: 'blue',
          icon: 'home-03',
          title: `You asked ${renterFirstName} to tour first`,
          detail: 'before applying',
          quote: truncateTraceText(payload.message, 90) || undefined,
        };
      default:
        return { tone: 'grey', icon: 'clock-fast-forward', title: humanizeTraceKey(payload.action) };
    }
  }

  // The kinds below normally attach to a message; they arrive standalone only from silent turns or
  // a failed message save. Keep what still informs the owner, hide the rest.
  if (trace.kind === ConversationTraceKind.STRATEGY) {
    const payload = trace.payload as unknown as StrategyTracePayload;
    if (payload.outcome !== 'escalated' || hiddenSteps.includes(payload.step)) {
      return null;
    }
    return {
      tone: 'blue',
      icon: 'question-mark-circle',
      title: 'Question sent to you',
      quote: truncateTraceText(payload.relatedText, 90) || undefined,
    };
  }

  if (trace.kind === ConversationTraceKind.SCHEDULING) {
    const payload = trace.payload as unknown as SchedulingTracePayload;
    return payload.phase === 'request_sent'
      ? {
          tone: 'amber',
          icon: 'calendar-plus-02',
          title: 'Tour request sent to you',
          detail: formatTraceDayTime(payload.time, timeZone),
        }
      : {
          tone: 'grey',
          icon: 'calendar-check-01',
          title: 'Tour time proposed',
          detail: formatTraceDayTime(payload.time, timeZone),
        };
  }

  if (trace.kind === ConversationTraceKind.ESCALATION_ANSWER) {
    const payload = trace.payload as unknown as EscalationAnswerTracePayload;
    return {
      tone: 'blue',
      icon: 'message-chat-circle',
      title: 'Your answer passed along',
      quote: truncateTraceText(payload.question, 90) || undefined,
    };
  }

  if (trace.kind === ConversationTraceKind.IDENTITY_VERIFICATION) {
    const payload = trace.payload as unknown as IdentityVerificationTracePayload;
    switch (payload.phase) {
      case 'verified':
        return {
          tone: 'green',
          icon: 'user-check-01',
          title: `${renterFirstName} verified their ID`,
          // The title already says "verified" — the detail only has to name the method, and the
          // row has 56ch before it wraps to a second line.
          detail: 'government ID + selfie',
        };
      case 'declined':
        return {
          tone: 'red',
          icon: 'x-close',
          title: 'ID verification declined',
          detail: `${renterFirstName}'s identity check did not pass`,
        };
      // link_sent normally rides the message as a chip; standalone it still earns a row.
      case 'link_sent':
        return {
          tone: 'blue',
          icon: 'user-check-01',
          title: 'ID verification requested',
          detail: `link sent to ${renterFirstName}`,
        };
      default:
        return { tone: 'grey', icon: 'user-check-01', title: humanizeTraceKey(String(payload.phase)) };
    }
  }

  if (trace.kind === ConversationTraceKind.APPLICATION_INVITE_SENT) {
    const payload = trace.payload as unknown as ApplicationInviteSentTracePayload;
    // Through an apply link nobody sent it: the renter applied and the application went out on its own.
    return payload?.via === 'apply_link'
      ? {
          tone: 'green',
          icon: 'link-01',
          title: `${renterFirstName} applied through your apply link`,
          detail: 'application sent automatically',
        }
      : {
          tone: 'green',
          icon: 'file-sent-02',
          title: 'You sent the application',
          detail: `${renterFirstName} was invited to apply`,
        };
  }

  if (trace.kind === ConversationTraceKind.APPLICATION_REQUESTED) {
    return {
      tone: 'amber',
      icon: 'file-question-02',
      title: `${renterFirstName} asked to apply through your apply link`,
      detail: 'the application waits for you to send it',
    };
  }

  if (trace.kind === ConversationTraceKind.APPLICATION_UPDATE_REQUESTED) {
    const payload = trace.payload as unknown as ApplicationUpdateRequestTracePayload;
    return {
      tone: 'amber',
      icon: 'file-question-02',
      title: 'You asked for more information',
      quote: truncateTraceText(payload.message, 90) || undefined,
    };
  }

  if (trace.kind === ConversationTraceKind.APPLICATION_SUBMITTED) {
    const payload = trace.payload as unknown as ApplicationSubmittedTracePayload;
    return {
      tone: 'green',
      icon: 'file-check-02',
      title: payload?.resubmitted
        ? `${renterFirstName} submitted the application again`
        : `${renterFirstName} submitted the application`,
      detail: payload?.resubmitted ? 'with the information you asked for' : 'ready for your review',
    };
  }

  if (trace.kind === ConversationTraceKind.FOLLOW_UP) {
    const payload = trace.payload as unknown as FollowUpTracePayload;
    const meta = followUpMeta[payload.followUpType];
    return {
      tone: 'amber',
      icon: 'message-chat-circle',
      title: 'Follow-up sent',
      detail: meta ? meta.piece(payload) : humanizeTraceKey(payload.followUpType).toLowerCase(),
    };
  }

  // Unknown kind from a newer BE: show a humanized row rather than crash or hide.
  return { tone: 'grey', icon: 'sparkle', title: humanizeTraceKey(trace.kind) };
}
